import React from "react";
import styled from "styled-components";
import { TertiaryButton } from "../UI/Button/Button";
import Divider from "../UI/Divider/Divider";
import { Device } from "../../../components/device";
import Anchor from "../../../components/Router/Anchor";
import { SalesStop } from "../../../typings/typings";

/*
Usage:
<Event
    time="17.00"
    name="Jeg er her!"
    location="Rundt i Aarhus"
    soldOut>
    Mere info her
</Event>
*/

interface CalendarEventWrapProps {
    soldOut: boolean;
}

const CalendarEventWrap = styled.div<CalendarEventWrapProps>`
    overflow: hidden;
    > div:not(.event__divider) {
        opacity: ${(props) => (props.soldOut ? ".45" : "1")};
        /* pointer-events: ${(props) => (props.soldOut ? "none" : "all")}; */
        .event__col-1 > * {
            opacity: ${(props) => (props.soldOut ? "1" : null)};
        }
    }

    .event__divider {
        opacity: .25;
    }

    .event__col-1 {
        width: 70%;
        padding-right: .5rem;
        ${Device.desktop} {
            width: 85%;
            display: flex;
            flex-flow: row;
            padding-right: 2rem;
            position: relative;
            top: 5px;
            > * {
                padding-left: .5rem;
                &:first-child {
                    padding-left: 0;
                }
            }
        }
    }
    .event__col-2 {
        width: 30%;
        text-align: center;
        ${Device.desktop} {
            width: 15%;
        }
        button,
        .event__sold-out-text {
            width: 100%;
            padding-top: .5rem;
            padding-bottom: .5rem;
            display: block;
            letter-spacing: 0.1em;
        }
    }
    .event__time {
        font-size: var(--text-xs, .7333rem);
        opacity: .5;
        ${Device.desktop} {
            width: 10%;
            opacity: 1;
            order: 0;
        }
        &:after {
            content: "|";
            padding-left: .33rem;
            padding-right: .33rem;
            position: relative;
            top: -1px;
            opacity: .5;
            ${Device.desktop} {
                display: none;
            }
        }
    }
    .event__location {
        font-size: var(--text-xs, .7333rem);
        opacity: .5;
        ${Device.desktop} {
            opacity: 1;
            order: 2;
        }
    }
    .event__content {
        flex-grow: 1;
    }
    .event__name {
        font-size: 1.1rem;
        word-break: break-word;
        
        ${Device.tablet} {
            font-size: 1rem;
        }
        ${Device.desktop} {
            font-size: 1.167rem;
            order: 1;
            width: 65%;
        }
    }
    .event__fewleft_text {
        opacity: .45;
    }
    .event__info {
        font-size: var(--text-xs, .7333rem);
        display: block;
        margin-top: 3px;
        opacity: .5;
        ${Device.desktop} {
        }
    }
    &:last-child {
        .event__divider {
            display: block;
        }
    }
    .text-xSmall {
        font-size: var(--text-xs, .7333rem) !important;
        letter-spacing: 0.025em;
    }
`;

interface CalendarEventProps {
    className?: string;
    time: string;
    name: string;
    notes: string;
    to: string;
    location: string;
    soldOut?: boolean;
    eventNo?: number;
    showNo?: number;
    buyOnClick: (eventNo: number, showNo?: number) => void;
    salesStop: SalesStop;
    tixlyEventImagePath?: string;
    tixlyFeaturedImagePath?: string;
    tixlyPosterImagePath?: string;
    purchaseUrls: any;
}

const CalendarEvent: React.FC<CalendarEventProps> = (props) => {
    const { purchaseUrls } = props;

    // const handleBuyOnClick = () =>
    // {
    //     if (eventNo)
    //     {
    //         props.buyOnClick(eventNo, showNo);
    //     }
    // };

    const handleByOnClick = () => {
        if (purchaseUrls && purchaseUrls[0]?.Link) {
            return (window.location.href = purchaseUrls[0]?.Link);
        } else {
            return "";
        }
    };

    const isCancelled = (salesStop?: SalesStop) => {
        if (salesStop && salesStop.reason && salesStop.reason.toLowerCase().includes("cancelled")) {
            return true;
        }
        return false;
    };

    const isFewLeft = (salesStop?: SalesStop) => {
        if (salesStop && salesStop.reason && salesStop.reason.toLowerCase().includes("fewtickets")) {
            return true;
        }
        return false;
    };

    const isSalesNotStarted = (salesStop?: SalesStop) => {
        if (salesStop && salesStop.reason && salesStop.reason.toLowerCase().includes("notscheduled")) {
            return true;
        }
        return false;
    };

    return (
        <CalendarEventWrap soldOut={!!props.soldOut || isCancelled(props.salesStop) || isSalesNotStarted(props.salesStop)}>
            <div className="flex flex-row justify-center sm:items-center py-4">
                <div className="event__col-1">
                    <span className="event__time">{props.time}</span>
                    <span className="event__location">{props.location}</span>
                    <div className="event__content">
                        <div className="event__name">
                            <Anchor to={props.to}>{props.name}</Anchor>
                        </div>
                        <div className="event__info">{props.notes}</div>
                    </div>
                </div>

                <div className="event__col-2 text-xSmall">
                    {isCancelled(props.salesStop) ? (
                        <span className="event__sold-out-text">Aflyst</span>
                    ) : isSalesNotStarted(props.salesStop) ? (
                        <span className="event__sold-out-text">Salget er ikke startet</span>
                    ) : isFewLeft(props.salesStop) && !props.soldOut && purchaseUrls[0]?.Link ? (
                        <span>
                            <TertiaryButton size="small" onClick={() => handleByOnClick()}>
                                Køb
                            </TertiaryButton>
                            <div className="event__fewleft_text">Kun få billetter</div>
                        </span>
                    ) : !props.soldOut && purchaseUrls[0]?.Link ? (
                        <TertiaryButton size="small" onClick={() => handleByOnClick()}>
                            Køb
                        </TertiaryButton>
                    ) : (
                        <span className="event__sold-out-text">Udsolgt</span>
                    )}
                </div>
            </div>

            <Divider className="event__divider" />
        </CalendarEventWrap>
    );
};

export default CalendarEvent;
